import Icon1 from "@assets/icons/Icon";
import { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { colors } from "sexy";
import SecondaryButton from "../buttons/SecondaryButton";

type TimePickerProps = {
  label?: string;
  value?: string | null; // HH:mm format or dayjs object
  onChange?: (time: string) => void; // Returns HH:mm format
  mandatory?: boolean;
  transparent?: boolean;
  disabled?: boolean;
  minHour?: number;
  maxHour?: number;
  minuteStep?: number; // 15, 30, etc.
  disabledSlots?: string[]; // HH:mm slots that should appear greyed out
};

const TimePicker = ({
  label = "Sélectionner une heure",
  value = null,
  onChange = () => {},
  mandatory = false,
  transparent = false,
  disabled = false,
  minHour = 8,
  maxHour = 20,
  minuteStep = 15,
  disabledSlots = [],
}: TimePickerProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [displayValue, setDisplayValue] = useState("");
  const [dropUp, setDropUp] = useState(false);
  const timePickerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Parse value to display format
  useEffect(() => {
    if (value) {
      // Handle both string "HH:mm" and dayjs objects
      const timeStr = typeof value === "string" ? value : value;
      setDisplayValue(timeStr);
    } else {
      setDisplayValue("");
    }
  }, [value]);

  // Handle click outside to close picker
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        timePickerRef.current &&
        !timePickerRef.current.contains(target) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(target)
      ) {
        setIsOpen(false);
      }
      if (
        timePickerRef.current &&
        !timePickerRef.current.contains(target) &&
        !dropdownRef.current
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Compute portal dropdown position when opening
  const [dropdownPos, setDropdownPos] = useState<{ top: number; left: number; width: number } | null>(null);

  useEffect(() => {
    if (isOpen && timePickerRef.current) {
      const updatePosition = () => {
        if (!timePickerRef.current) return;
        const rect = timePickerRef.current.getBoundingClientRect();
        const ddHeight = dropdownRef.current?.offsetHeight || 380;
        const spaceBelow = window.innerHeight - rect.bottom;
        const spaceAbove = rect.top;
        const goUp = spaceBelow < ddHeight && spaceAbove > spaceBelow;
        setDropUp(goUp);
        let top = goUp ? rect.top - ddHeight - 4 : rect.bottom + 4;
        // Clamp to viewport
        if (top < 4) top = 4;
        if (top + ddHeight > window.innerHeight - 4) top = window.innerHeight - ddHeight - 4;
        setDropdownPos({
          top,
          left: rect.left,
          width: rect.width,
        });
      };

      // Initial position, then re-measure after browser completes layout
      updatePosition();
      requestAnimationFrame(() => requestAnimationFrame(updatePosition));
      window.addEventListener("resize", updatePosition);
      window.addEventListener("scroll", updatePosition, true);

      return () => {
        window.removeEventListener("resize", updatePosition);
        window.removeEventListener("scroll", updatePosition, true);
      };
    } else {
      setDropdownPos(null);
    }
  }, [isOpen]);

  // Generate time slots
  const generateTimeSlots = (): string[] => {
    const slots: string[] = [];
    const totalMinStart = minHour * 60;
    const totalMinEnd = maxHour * 60;
    for (let totalMin = totalMinStart; totalMin <= totalMinEnd; totalMin += minuteStep) {
      const hour = Math.floor(totalMin / 60);
      const minute = Math.floor(totalMin % 60);
      const timeStr = `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;
      slots.push(timeStr);
    }
    return slots;
  };

  const timeSlots = generateTimeSlots();

  const handleSelectTime = (time: string) => {
    setDisplayValue(time);
    onChange(time);
    setIsOpen(false);
  };

  // Handle manual input
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let val = e.target.value.replace(/\D/g, ""); // Remove non-numeric

    if (val.length > 4) val = val.slice(0, 4); // Limit to 4 digits

    // Auto-insert `:` when there are at least 3 digits
    if (val.length >= 3) {
      val = `${val.slice(0, 2)}:${val.slice(2)}`;
    }

    setDisplayValue(val);

    // Validate when full "HH:mm" is entered
    if (val.length === 5) {
      const [hourStr, minuteStr] = val.split(":");
      const hour = parseInt(hourStr, 10);
      const minute = parseInt(minuteStr, 10);

      if (
        hour >= 0 &&
        hour <= 23 &&
        minute >= 0 &&
        minute <= 59 &&
        hour >= minHour &&
        hour <= maxHour
      ) {
        // Round to nearest step (supports fractional steps like 7.5)
        const totalMinutes = hour * 60 + minute;
        const roundedTotal = Math.round(totalMinutes / minuteStep) * minuteStep;
        let finalHour = Math.floor(roundedTotal / 60);
        let finalMinute = Math.floor(roundedTotal % 60);

        // Clamp to valid range
        if (finalHour < minHour) {
          finalHour = minHour;
          finalMinute = 0;
        }
        if (finalHour > maxHour) {
          finalHour = maxHour;
          finalMinute = 0;
        }

        const finalTime = `${String(finalHour).padStart(2, "0")}:${String(finalMinute).padStart(2, "0")}`;
        setDisplayValue(finalTime);
        onChange(finalTime);
      }
    }
  };

  const handleInputBlur = () => {
    // If input is incomplete, clear it
    if (displayValue.length > 0 && displayValue.length < 5) {
      setDisplayValue("");
      onChange("");
    }
  };

  const isSelected = (time: string): boolean => {
    return displayValue === time;
  };

  const has_value = displayValue !== null && displayValue !== "";

  return (
    <div className="flex flex-col gap-1">
      <div className="text-h4 text-neutral-400 ">{label}</div>{" "}
      <div
        className={`${transparent ? "" : "bg-white shadow-sm"} rounded-xl relative w-full select-none`}
        ref={timePickerRef}
      >
        {/* Input Field */}
        <div
          tabIndex={0}
          onFocus={() => {
            setIsOpen(true);
          }}
          className={`px-3 w-full text-h2 rounded-xl cursor-pointer outline-none ${
            isOpen
              ? "ring-neutral-300 ring-4"
              : mandatory && !has_value
                ? "border-2 border-red-300"
                : "outline-none border"
          } relative flex flex-row py-3 h-fit ${has_value ? "items-end" : "items-center"} ${
            disabled ? "bg-neutral-100 cursor-not-allowed" : ""
          }`}
        >
          <input
            type="text"
            className="w-full text-h2 bg-transparent rounded-xl focus:outline-none cursor-pointer border-0"
            placeholder={label}
            value={displayValue}
            onChange={handleInputChange}
            onBlur={handleInputBlur}
            disabled={disabled}
            maxLength={5}
          />
          <div className="absolute right-3 top-1/3 text-gray-400 cursor-pointer pointer-events-none">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-4 w-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>
        </div>
      </div>

      {/* Time Dropdown — rendered via portal */}
      {isOpen && !disabled && dropdownPos && createPortal(
        <div
          ref={dropdownRef}
          className="bg-white rounded-xl shadow-lg border border-gray-200"
          style={{
            position: "fixed",
            top: dropdownPos.top,
            left: Math.min(dropdownPos.left, window.innerWidth - Math.max(dropdownPos.width, minuteStep < 15 ? 520 : 280) - 8),
            width: Math.min(Math.max(dropdownPos.width, minuteStep < 15 ? 520 : 280), window.innerWidth - 16),
            zIndex: 9999,
          }}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-3 border-b">
            <div className="text-h2 font-medium text-neutral-700">
              Choisir une heure
            </div>
            <button
              onClick={() => setIsOpen(false)}
              className="text-neutral-400 hover:text-neutral-600"
            >
              <svg
                className="h-4 w-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          {/* Time slots grid */}
          <div className="p-2 max-h-64 overflow-y-auto">
            <div className={`grid gap-1 ${minuteStep < 15 ? "grid-cols-8" : "grid-cols-4 gap-2"}`}>
              {timeSlots.map((time) => {
                const isSlotDisabled = disabledSlots.includes(time);
                return (
                  <button
                    key={time}
                    onClick={() => !isSlotDisabled && handleSelectTime(time)}
                    disabled={isSlotDisabled}
                    className={`
                    py-2 px-3 text-h2 rounded-lg transition-all
                    ${
                      isSelected(time)
                        ? "bg-neutral-900 text-white hover:bg-neutral-600"
                        : isSlotDisabled
                          ? "bg-neutral-100 text-neutral-300 cursor-default"
                          : "hover:bg-neutral-50 text-neutral-700"
                    }
                  `}
                  >
                    {time}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Footer with quick actions */}
          <div className="border-t p-2 flex justify-between items-center">
            <SecondaryButton
              label="Maintenant"
              onClick={() => {
                const now = new Date();
                const totalMinutes = now.getHours() * 60 + now.getMinutes();

                // Round to nearest step (supports fractional steps like 7.5)
                const roundedTotal = Math.round(totalMinutes / minuteStep) * minuteStep;
                let finalHour = Math.floor(roundedTotal / 60);
                let finalMinute = Math.floor(roundedTotal % 60);

                // Clamp to valid range
                if (finalHour < minHour) {
                  finalHour = minHour;
                  finalMinute = 0;
                } else if (finalHour > maxHour) {
                  finalHour = maxHour;
                  finalMinute = 0;
                }

                const nowTime = `${String(finalHour).padStart(2, "0")}:${String(finalMinute).padStart(2, "0")}`;
                handleSelectTime(nowTime);
              }}
              variant="small"
            ></SecondaryButton>
            <button
              onClick={() => {
                setDisplayValue("");
                onChange("");
                setIsOpen(false);
              }}
              className="text-xs text-red-500 hover:text-red-700"
            >
              <Icon1 name="effacer2" size={20} color={colors.neutral_400} />
            </button>
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
};

export default TimePicker;
